/**
 * Custom Problem Manager
 * Manages creation, storage, and retrieval of custom problems created by parents/teachers.
 * Uses localStorage.
 */

const CustomProblemManager = {
    STORAGE_KEY: 'detectiveMath_customProblems',

    /**
     * Get all custom problems
     */
    getAll() {
        try {
            const data = localStorage.getItem(this.STORAGE_KEY);
            return data ? JSON.parse(data) : [];
        } catch (error) {
            console.error('Error loading custom problems:', error);
            return [];
        }
    },

    /**
     * Save a new problem
     */
    saveProblem(problemData) {
        const problems = this.getAll();

        // Add ID and timestamp
        const newProblem = {
            id: 'custom_' + Date.now(),
            created: new Date().toISOString(),
            ...problemData
        };

        problems.push(newProblem);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(problems));
        console.log('✅ Custom problem saved:', newProblem.id);

        return newProblem;
    },

    /**
     * Delete a problem by ID
     */
    deleteProblem(id) {
        let problems = this.getAll();
        problems = problems.filter(p => p.id !== id);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(problems));
        console.log('🗑️ Custom problem deleted:', id);
    },

    /**
     * Delete All Problems
     */
    deleteAll() {
        localStorage.removeItem(this.STORAGE_KEY);
        console.log('🗑️ All custom problems deleted.');
    },

    /**
     * Convert custom problem format to internal ExcelLoader format
     */
    convertToInternalFormat(customProblem) {
        // Must match the object structure in ExcelLoader.processWorkbook

        // Analyze statement for numbers and answer if not provided
        let analysis = { isValid: true };
        if (window.ExcelLoader) {
            analysis = ExcelLoader.analyzeProblemStatement(customProblem.statement);
        }

        return {
            isCustom: true, // Flag to identify origin
            id: customProblem.id,
            level: customProblem.level, // 'beginner', 'advanced', 'expert'
            type: customProblem.type, // 'sum', 'subtraction'
            statement: {
                'es-ES': customProblem.statement,
                'en-US': customProblem.statement,
                'ca-ES': customProblem.statement
            },
            contextId: customProblem.contextId, // e.g., 'supermercado'
            contextName: {
                'es-ES': 'Personalizado', // Or map from ID
                'en-US': 'Custom',
                'ca-ES': 'Personalitzat'
            },
            question: customProblem.question || (window.ExcelLoader ? ExcelLoader.extractQuestion(customProblem.statement) : ''),
            solution: {
                answer: customProblem.answer ?? analysis.expectedAnswer ?? 0,
                operation: customProblem.type === 'sum' ? 'addition' : 'subtraction'
            },
            data: {
                useful: customProblem.data || analysis.numbers || [],
                names: customProblem.names || analysis.names || []
            }
        };
    },

    /**
     * Export all custom problems to a downloadable Excel file
     */
    exportToExcel() {
        if (!window.XLSX) {
            alert('Error: Librería de Excel no cargada.');
            return;
        }

        const problems = this.getAll();
        if (problems.length === 0) {
            alert('No hay problemas nuevos para exportar.');
            return;
        }

        // Prepare data for Excel (Headers matching the original structure)
        const data = [
            ['Nivel', 'Operación', 'Numero', 'Enunciado', 'Nombre_Imagen', 'Resultado', 'Calculo']
        ];

        problems.forEach(p => {
            const analysis = window.ExcelLoader ? ExcelLoader.analyzeProblemStatement(p.statement, p.type) : { numbers: [], expectedAnswer: '-' };
            data.push([
                p.level.toUpperCase(),
                p.type.toUpperCase(),
                analysis.numbers[0] || '',
                p.statement,
                '', // No image for custom problems usually
                analysis.expectedAnswer || '',
                ''  // Optional calculation info
            ]);
        });

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(data);

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'NUEVOS PROBLEMAS');

        // Trigger download
        XLSX.writeFile(wb, 'NUEVOS PROBLEMAS.xlsx');
        console.log('📊 Custom problems exported to Excel.');
    },

    /**
     * Import problems from a selected Excel file
     * @param {File} file - The Excel file to read
     */
    importFromExcel(file) {
        return new Promise((resolve, reject) => {
            if (!window.XLSX) {
                return reject('Librería de Excel no cargada.');
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet);

                    if (jsonData.length === 0) {
                        return reject('El archivo está vacío.');
                    }

                    let importedCount = 0;
                    jsonData.forEach(row => {
                        // Validate minimum fields
                        if (row.Enunciado && row.Operación) {
                            const type = row.Operación.toLowerCase().includes('resta') ? 'subtraction' : 'sum';
                            const level = row.Nivel?.toLowerCase() || 'beginner';

                            // Map level text back to ID if necessary
                            let levelId = 'beginner';
                            if (level.includes('avanza') || level.includes('intermedio') || level.includes('medio')) levelId = 'advanced';
                            if (level.includes('experto') || level.includes('dif') || level.includes('genio')) levelId = 'expert';

                            this.saveProblem({
                                contextId: 'personalizado', // Default context for imports
                                level: levelId,
                                type: type,
                                statement: row.Enunciado
                            });
                            importedCount++;
                        }
                    });

                    resolve(importedCount);
                } catch (error) {
                    console.error('Error parsing Excel:', error);
                    reject('Error al procesar el archivo Excel.');
                }
            };
            reader.onerror = () => reject('Error al leer el archivo.');
            reader.readAsArrayBuffer(file);
        });
    }
};

window.CustomProblemManager = CustomProblemManager;
